import { motion } from "framer-motion";
import { useEffect, useState } from "react";

interface ToyShelfProps {
  active: boolean;
  onPlaced: () => void;
}

const TOY_POOL = [
  "🐻",
  "🦁",
  "🐸",
  "🎪",
  "🦄",
  "🦊",
  "🐼",
  "🧸",
  "🚗",
  "⚽",
  "🎯",
  "🪀",
  "🛸",
  "🚂",
  "🧩",
];

const pickRandomToys = (count: number): string[] => {
  const pool = [...TOY_POOL];
  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, count);
};

const ToyShelf = ({ active, onPlaced }: ToyShelfProps) => {
  const [landed, setLanded] = useState(false);
  const [placed, setPlaced] = useState(false);
  const [existingToys, setExistingToys] = useState<string[]>(() => pickRandomToys(5));

  useEffect(() => {
    if (!active) {
      setLanded(false);
      setPlaced(false);
      return;
    }
    setExistingToys(pickRandomToys(5));
    const t1 = setTimeout(() => setLanded(true), 800);
    const t2 = setTimeout(() => {
      setPlaced(true);
      onPlaced();
    }, 1800);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [active, onPlaced]);

  return (
    <motion.div
      className="mx-auto w-full max-w-xs rounded-2xl border border-slate-200 bg-white p-5 shadow-lg"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h3 className="mb-3 text-center text-sm font-bold text-slate-500">My Toy Collection</h3>
      <div className="grid grid-cols-3 justify-items-center gap-3">
        {existingToys.map((toy, i) => (
          <motion.div
            key={`${toy}-${i}`}
            className="flex h-16 w-16 items-center justify-center rounded-xl bg-slate-100 text-2xl shadow-sm"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.08 }}
          >
            {toy}
          </motion.div>
        ))}

        <motion.div
          className={`flex h-16 w-16 items-center justify-center rounded-xl border-2 text-2xl transition-colors ${
            placed
              ? "border-emerald-500 bg-emerald-100 shadow-md"
              : "border-dashed border-blue-400 bg-blue-50"
          }`}
          animate={
            !placed && active
              ? {
                  borderColor: [
                    "rgba(96,165,250,0.4)",
                    "rgba(96,165,250,1)",
                    "rgba(96,165,250,0.4)",
                  ],
                }
              : placed
                ? { scale: [1, 1.15, 1] }
                : {}
          }
          transition={placed ? { duration: 0.4, type: "spring" } : { duration: 1, repeat: Infinity }}
        >
          {landed ? (
            <motion.div
              initial={placed ? { scale: 0, rotate: 360, y: -30 } : { scale: 0 }}
              animate={{ scale: 1, rotate: 0, y: 0 }}
              transition={{ type: "spring", bounce: 0.6, duration: 0.6 }}
              className="text-3xl"
            >
              ⚽
            </motion.div>
          ) : (
            <motion.span
              className="text-lg font-bold text-blue-400"
              animate={{ opacity: [0.3, 0.7, 0.3] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              ?
            </motion.span>
          )}
        </motion.div>
      </div>

      {active && !landed && (
        <motion.div
          className="mt-3 flex justify-center"
          initial={{ x: 100, y: -40, opacity: 0 }}
          animate={{ x: 0, y: 0, opacity: [0, 1, 1, 0] }}
          transition={{ duration: 0.8 }}
        >
          <span className="text-3xl">🚀</span>
        </motion.div>
      )}
    </motion.div>
  );
};

export default ToyShelf;